import { useEffect, useRef, useState } from 'react';
import {
  OndemandAdminClient,
  OndemandAdminRequestStatus,
} from '@internal/ondemand-admin-api-helper';

export const POLL_INTERVAL_MS = 3000;
export const POLL_MAX_DURATION_MS = 3 * 60 * 1000;
const MAX_CONSECUTIVE_ERRORS = 3;

// The status endpoint does not exist (old API) or is not usable for this
// user, so retrying will not help.
const UNAVAILABLE_STATUS_CODES = [400, 403, 404];
const TERMINAL_STAGES = ['active', 'ended', 'failed'];

export type RequestProgressPhase =
  | 'idle'
  | 'polling'
  | 'complete'
  | 'timedOut'
  | 'unavailable';

export interface UseRequestProgressOptions {
  client: Pick<OndemandAdminClient, 'getRequestStatus'>;
  workloadName: string;
  accountId: string;
  // Polling only starts when a request id is provided
  requestId?: string;
  intervalMs?: number;
  maxDurationMs?: number;
}

export interface RequestProgress {
  status?: OndemandAdminRequestStatus;
  phase: RequestProgressPhase;
}

export const useRequestProgress = ({
  client,
  workloadName,
  accountId,
  requestId,
  intervalMs = POLL_INTERVAL_MS,
  maxDurationMs = POLL_MAX_DURATION_MS,
}: UseRequestProgressOptions): RequestProgress => {
  const [progress, setProgress] = useState<RequestProgress>({ phase: 'idle' });
  // The card builds a new client on every render, keep the latest one in a ref
  // so that polling is not restarted by re-renders
  const clientRef = useRef(client);
  clientRef.current = client;

  useEffect(() => {
    if (!requestId) {
      setProgress(previous =>
        previous.phase === 'idle' ? previous : { phase: 'idle' },
      );
      return undefined;
    }

    const id: string = requestId;
    let cancelled = false;
    let pollTimer: ReturnType<typeof setTimeout> | undefined;
    let consecutiveErrors = 0;
    let hasReceivedStatus = false;
    const capTimer = setTimeout(() => {
      if (!cancelled) {
        // Keep the last known status so the UI can still show it
        cancelled = true;
        clearTimeout(pollTimer);
        setProgress(previous => ({ ...previous, phase: 'timedOut' }));
      }
    }, maxDurationMs);

    const stop = (next: RequestProgress) => {
      cancelled = true;
      clearTimeout(pollTimer);
      clearTimeout(capTimer);
      setProgress(next);
    };

    function recordError() {
      consecutiveErrors += 1;
      if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
        stop({ phase: 'unavailable' });
        return;
      }
      pollTimer = setTimeout(poll, intervalMs);
    }

    // Each poll only schedules the next one once it has finished, so requests
    // never overlap
    async function poll() {
      try {
        const { response, json } = await clientRef.current.getRequestStatus({
          workloadName,
          accountId,
          requestId: id,
        });
        if (cancelled) {
          return;
        }

        if (!response.ok) {
          if (
            !hasReceivedStatus &&
            UNAVAILABLE_STATUS_CODES.includes(response.status)
          ) {
            stop({ phase: 'unavailable' });
            return;
          }
          recordError();
          return;
        }

        const status = (await json()) as OndemandAdminRequestStatus;
        if (cancelled) {
          return;
        }
        if (!status?.stage) {
          recordError();
          return;
        }

        hasReceivedStatus = true;
        consecutiveErrors = 0;
        if (status.terminal || TERMINAL_STAGES.includes(status.stage)) {
          stop({ phase: 'complete', status });
          return;
        }
        setProgress({ phase: 'polling', status });
        pollTimer = setTimeout(poll, intervalMs);
      } catch (e) {
        if (!cancelled) {
          recordError();
        }
      }
    }

    setProgress({ phase: 'polling' });
    poll();

    return () => {
      cancelled = true;
      clearTimeout(pollTimer);
      clearTimeout(capTimer);
    };
  }, [workloadName, accountId, requestId, intervalMs, maxDurationMs]);

  return progress;
};
